import { QueryClient, QueryFunction } from "@tanstack/react-query";

export function dispatchMissingKeyEventIfNeeded(status: number, body: string) {
  if (
    (status === 400 || status === 503) &&
    /kein.*api.?key|kein.*key.*verf|kein.*key.*gespeichert|no.*api.?key/i.test(body)
  ) {
    try {
      const service = /openai/i.test(body) ? "openai" : /anthropic|claude/i.test(body) ? "anthropic" : undefined;
      window.dispatchEvent(new CustomEvent("missing-api-key", { detail: { service, message: body } }));
    } catch {
      // non-browser environment
    }
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    dispatchMissingKeyEventIfNeeded(res.status, text);
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  signal?: AbortSignal,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    signal,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
